import React, { useState, useEffect, useMemo } from "react";
import { Briefcase, User } from "lucide-react";
import PremiumModal from "./PremiumModal";
import SearchableSelect from "./SearchableSelect";
import QuickCreateClientModal from "./QuickCreateClientModal";
import { mattersApi, clientsApi, usersApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import { PRACTICE_AREA_OPTIONS } from "../onboarding/steps/PracticeAreaStep";

interface QuickCreateMatterModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (matter: { _id: string; title: string; matterNumber: string }) => void;
  preselectedClientId?: string;
}

const QuickCreateMatterModal: React.FC<QuickCreateMatterModalProps> = ({
  open,
  onClose,
  onCreated,
  preselectedClientId,
}) => {
  const { firmData, userData } = useUserDataContext();

  // Filter practice areas to only those the firm selected during onboarding
  const practiceAreaOptions = useMemo(() => {
    const firmAreas = firmData?.practiceAreas;
    if (!firmAreas || firmAreas.length === 0) {
      // Fallback: show all options if no areas configured
      return PRACTICE_AREA_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
    }
    const filtered = PRACTICE_AREA_OPTIONS
      .filter((o) => firmAreas.includes(o.value))
      .map((o) => ({ value: o.value, label: o.label }));
    // Always include "other" as a fallback
    if (!filtered.some((o) => o.value === "other")) {
      filtered.push({ value: "other", label: "Other" });
    }
    return filtered;
  }, [firmData?.practiceAreas]);

  // Pre-fill to first firm practice area
  const defaultArea = practiceAreaOptions[0]?.value || "other";

  const [form, setForm] = useState({
    title: "",
    clientId: preselectedClientId || "",
    responsibleLawyerId: userData._id || "",
    practiceArea: defaultArea,
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [clients, setClients] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [showQuickClient, setShowQuickClient] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm((prev) => ({ ...prev, practiceArea: defaultArea }));
    Promise.all([
      clientsApi.list().then((res) => setClients(res.data?.data || res.data || [])),
      usersApi.list().then((res) => {
        const userList = res.data?.data || res.data || [];
        setUsers(userList);
        // Auto-select responsible team member if only one user
        if (userList.length === 1) {
          setForm((prev) => ({ ...prev, responsibleLawyerId: userList[0]._id }));
        }
      }),
    ]);
    if (preselectedClientId) {
      setForm((prev) => ({ ...prev, clientId: preselectedClientId }));
    }
  }, [open, preselectedClientId, defaultArea]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.responsibleLawyerId) return;
    setCreating(true);
    setError("");
    try {
      const res = await mattersApi.create({
        title: form.title.trim(),
        clientId: form.clientId || null,
        responsibleLawyerId: form.responsibleLawyerId,
        practiceArea: form.practiceArea,
      });
      const newMatter = res.data;
      onCreated({
        _id: newMatter._id,
        title: newMatter.title,
        matterNumber: newMatter.matterNumber,
      });
      setForm({ title: "", clientId: preselectedClientId || "", responsibleLawyerId: "", practiceArea: defaultArea });
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.msg || err.message || "Failed to create matter";
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
    <PremiumModal
      open={open}
      onClose={onClose}
      title="New Matter"
      icon={Briefcase}
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="quick-create-matter"
            disabled={creating || !form.title.trim() || !form.clientId || !form.responsibleLawyerId}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            {creating ? "Creating..." : "Create Matter"}
          </button>
        </div>
      }
    >
      <form id="quick-create-matter" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
          <input
            type="text"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            placeholder="Smith v. Johnson"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
          <SearchableSelect
            options={clients.map((c: any) => ({ value: c._id, label: c.displayName }))}
            value={form.clientId || null}
            onChange={(val) => setForm({ ...form, clientId: val || "" })}
            placeholder="Select a client"
            required
            icon={User}
            addNew={{
              label: "New Client",
              onAdd: () => setShowQuickClient(true),
            }}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Responsible Team Member *</label>
          <SearchableSelect
            options={users.map((u: any) => ({ value: u._id, label: u.name }))}
            value={form.responsibleLawyerId || null}
            onChange={(val) => setForm({ ...form, responsibleLawyerId: val || "" })}
            placeholder="Select a team member"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Practice Area</label>
          <SearchableSelect
            options={practiceAreaOptions}
            value={form.practiceArea || null}
            onChange={(val) => setForm({ ...form, practiceArea: val || "other" })}
            placeholder="Select practice area"
          />
        </div>
      </form>
    </PremiumModal>

    <QuickCreateClientModal
      open={showQuickClient}
      onClose={() => setShowQuickClient(false)}
      onCreated={async (newClient) => {
        // Refresh clients list and auto-select
        try {
          const res = await clientsApi.list();
          setClients(res.data?.data || res.data || []);
        } catch {}
        setForm((prev) => ({ ...prev, clientId: newClient._id }));
      }}
    />
    </>
  );
};

export default QuickCreateMatterModal;
